import React, { useEffect, useState } from "react";
import AHButton from "../__shared__/StyleGuide/AHButton";
import {
    BROADCAST_CHANNEL_LOGOUT_MSG,
    BROADCAST_CHANNEL_NAME,
    EMPTY_STRING,
    MAX_EMAIL_LENGTH,
    stepPath,
    SUCCESS,
} from "../../constants";
import AHAlphaField from "../__shared__/StyleGuide/AHAlphaField";
import { jwtDecode } from "jwt-decode";
import { useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { handleLogout as handleLogoutAction } from "../Login/actions";
import {
    INVALID_TOKEN_TEXT,
    LINK_ACCOUNT_BTN,
    linkAccountErrorMsgs,
    LOGOUT_BTN_HREF_TEXT,
    LOGOUT_BTN_TEXT,
} from "./constants";
import { verifyAccountLinking } from "../../helpers/backend_api";
import { fields } from "../__shared__/StyleGuide/NewPassword/constants";
import SpinLoader from "../__shared__/SpinLoader";
import UnfoldLogo from "../../ui/logos/UnfoldLogo";

function SSOLinkAccounts() {
    const dispatch = useDispatch();
    const auth0 = useAuth0();

    const [password, setPassword] = useState(EMPTY_STRING);
    const [email, setEmail] = useState(EMPTY_STRING);
    const [auth0State, setAuth0State] = useState(EMPTY_STRING);
    const [sessionToken, setSessionToken] = useState(EMPTY_STRING);

    const [errorPasswordMsg, setErrorPasswordMsg] = useState(EMPTY_STRING);
    const [isLinkAccountLoading, setIsLinkAccountLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isTokenInvalid, setIsTokenInvalid] = useState(false);

    function handleLogoutClick(e) {
        e.preventDefault();
        dispatch(handleLogoutAction());
        const logoutChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        logoutChannel.postMessage(BROADCAST_CHANNEL_LOGOUT_MSG);
        auth0.logout({
            logoutParams: { returnTo: window.location.origin + stepPath.LOGIN },
        });
    }

    useEffect(() => {
        try {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const sessionToken = urlParams.get("session_token");
            const state = urlParams.get("state");

            if (!sessionToken?.length || !state?.length) {
                setIsTokenInvalid(true);
                setIsPageLoading(false);
            }

            setSessionToken(sessionToken);
            const decodedToken = jwtDecode(sessionToken);
            if (decodedToken?.current_identity?.email) {
                const userEmail = decodedToken.current_identity.email;
                if (userEmail) {
                    setEmail(userEmail);
                }
                setAuth0State(state || EMPTY_STRING);
            }
            setIsPageLoading(false);
        } catch (error) {
            setIsTokenInvalid(true);
            setIsPageLoading(false);
        }
    }, []);

    function onPasswordChange(label, value) {
        setPassword(value);
    }

    async function linkAccounts() {
        if (password === EMPTY_STRING) {
            setErrorPasswordMsg(linkAccountErrorMsgs.PASSWORD_EMPTY);
            return;
        }

        const verifyAccountLinkingResponse = await verifyAccountLinking({
            username: email,
            password: password,
            sessionToken: sessionToken,
            auth0State: auth0State,
        });

        if (verifyAccountLinkingResponse?.status !== SUCCESS) {
            setErrorPasswordMsg(linkAccountErrorMsgs.PASSWORD_INCORRECT);
            return;
        }

        setIsLinkAccountLoading(true);
        if (verifyAccountLinkingResponse?.redirectUrl) {
            window.location.href = verifyAccountLinkingResponse?.redirectUrl;
        } else {
            setErrorPasswordMsg(linkAccountErrorMsgs.REDIRECT_URL_MISSING);
            setIsLinkAccountLoading(false);
        }
    }

    function renderBackToLoginBtn() {
        return (
            <div>
                {LOGOUT_BTN_TEXT}
                <button
                    id="back-to-login-btn"
                    className="text-primary-500 mt-8 outline-hidden hover:underline"
                    onClick={handleLogoutClick}
                >
                    {LOGOUT_BTN_HREF_TEXT}
                </button>
                .
            </div>
        );
    }

    function renderLinkAccountForm() {
        return (
            <div className="min-w-[600px] rounded-xl bg-white px-12 py-10 text-center">
                <p>
                    Enter the password for{" "}
                    <span className="font-medium">{email}</span> to link
                    accounts.
                </p>
                <AHAlphaField
                    label={fields.ENTER_PASSWORD}
                    value={password}
                    placeholder="Enter Password"
                    onChange={onPasswordChange}
                    id="link-accounts-password"
                    className="ah-alpha-field-wrapper mt-8"
                    type="password"
                    maxLength={MAX_EMAIL_LENGTH}
                    showLabel={false}
                />
                <div className="mt-8 text-[#a0151f]">{errorPasswordMsg}</div>
                <AHButton
                    id="link-accounts-btn"
                    isOutline={false}
                    isSecondary={false}
                    size={"large"}
                    isDynamic={false}
                    text={
                        isLinkAccountLoading ? (
                            <SpinLoader
                                display={"inline-block"}
                                height={"25px"}
                                width={"25px"}
                                animationDuration={"1s"}
                            />
                        ) : (
                            LINK_ACCOUNT_BTN
                        )
                    }
                    className={"center mt-8"}
                    onClick={(e) => {
                        e.preventDefault();
                        linkAccounts();
                    }}
                />
                {renderBackToLoginBtn()}
            </div>
        );
    }

    function renderInvalidTokenMessage() {
        return (
            <div className="rounded-xl bg-white px-16 py-16 text-center">
                <p className="text-[#a0151f]">{INVALID_TOKEN_TEXT}</p>
                {renderBackToLoginBtn()}
            </div>
        );
    }

    function renderLoadedPage() {
        return (
            <div className="-mt-10 flex h-[calc(100vh+40px)] w-screen flex-col items-center justify-center overflow-y-auto bg-[#f8f9fa] pb-10">
                <UnfoldLogo />
                {isTokenInvalid
                    ? renderInvalidTokenMessage()
                    : renderLinkAccountForm()}
            </div>
        );
    }

    function renderLoadingPage() {
        return (
            <div
                id="loading-screen"
                className={"flex h-screen w-screen items-center justify-center"}
            >
                <SpinLoader
                    width={"50px"}
                    height={"50px"}
                    display={"block"}
                    loaderWidth={"3px"}
                    fgColor="var(--avendaTeal1)"
                />
            </div>
        );
    }

    return (
        <div>{isPageLoading ? renderLoadingPage() : renderLoadedPage()}</div>
    );
}

export default SSOLinkAccounts;
